import axios from 'axios';

/**
 * Reusable mixin for multi-select dropdown components that handles:
 * - Individual dropdown state management
 * - Option caching and debouncing
 * - Duplicate prevention
 * - Proper cleanup
 */
export default {
    data() {
        return {
            // Global options cache to avoid redundant API calls
            globalOptionsCache: new Map(),
            // Individual options for each dropdown
            dropdownOptions: new Map(),
            // Selected items cache for quick lookup
            selectedItemsCache: new Map(),
            // Debounce timers for API calls
            debounceTimers: new Map(),
        };
    },
    
    methods: {
        /**
         * Initialize options for all existing dropdowns
         */
        async initializeOptions(apiEndpoint) {
            // Load initial options for all existing dropdowns
            await this.fetchOptions("", apiEndpoint);
            
            // Initialize options for existing dropdowns
            this.itemList.forEach((_, index) => {
                this.initializeDropdownOptions(index);
            });
        },
        
        /**
         * Initialize options for a specific dropdown
         */
        initializeDropdownOptions(dropdownIndex) {
            const item = this.itemList[dropdownIndex];
            if (!item) return;
            
            const uniqueId = item[this.uniqueIdField];
            if (!uniqueId) return;
            
            // Initialize with cached options
            const cachedOptions = this.globalOptionsCache.get("") || [];
            this.dropdownOptions.set(uniqueId, [...cachedOptions]);
            
            // Ensure selected item is included
            this.ensureSelectedItemInOptions(dropdownIndex);
        },
        
        /**
         * Fetch options for a specific dropdown with debouncing
         */
        async fetchOptionsForDropdown(event, dropdownIndex, apiEndpoint) {
            const item = this.itemList[dropdownIndex];
            if (!item) return;
            
            const uniqueId = item[this.uniqueIdField];
            const keyword = event.value || "";
            
            // Clear existing debounce timer
            if (this.debounceTimers.has(uniqueId)) {
                clearTimeout(this.debounceTimers.get(uniqueId));
            }
            
            // Set new debounce timer
            const timer = setTimeout(async () => {
                try {
                    const options = await this.fetchOptions(keyword, apiEndpoint);
                    // Update options for this specific dropdown
                    this.dropdownOptions.set(uniqueId, options);
                    // Ensure selected item is still available
                    this.ensureSelectedItemInOptions(dropdownIndex);
                } catch (error) {
                    console.error("Error fetching options for dropdown:", error);
                }
            }, 300); // 300ms debounce
            
            this.debounceTimers.set(uniqueId, timer);
        },
        
        /**
         * Fetch options from API with caching
         */
        async fetchOptions(keyword, apiEndpoint) {
            // Check cache first
            if (this.globalOptionsCache.has(keyword)) {
                return this.globalOptionsCache.get(keyword);
            }
            
            try {
                const response = await axios.post(apiEndpoint, {
                    params: {
                        keyword: keyword,
                    },
                });
                
                if (response.data && Array.isArray(response.data)) {
                    // Cache the results
                    this.globalOptionsCache.set(keyword, response.data);
                    return response.data;
                }
            } catch (error) {
                console.error("Error fetching options:", error);
            }
            
            return [];
        },
        
        /**
         * Get filtered options for a specific dropdown
         */
        getOptionsForDropdown(dropdownIndex) {
            const item = this.itemList[dropdownIndex];
            if (!item || !item[this.uniqueIdField]) return [];
            
            const options = this.dropdownOptions.get(item[this.uniqueIdField]) || [];
            
            // Filter out options that are already selected in other dropdowns
            const filteredOptions = options.filter(option => {
                const isSelectedElsewhere = this.itemList.some((listItem, idx) => 
                    idx !== dropdownIndex && listItem.id === option.id
                );
                return !isSelectedElsewhere;
            });
            
            // Always include the currently selected item for this dropdown
            const currentlySelected = this.selectedItemsCache.get(item.id);
            if (currentlySelected && !filteredOptions.some(opt => opt.id === currentlySelected.id)) {
                filteredOptions.unshift(currentlySelected);
            }
            
            return filteredOptions;
        },
        
        /**
         * Ensure selected item is available in dropdown options
         */
        ensureSelectedItemInOptions(dropdownIndex) {
            const item = this.itemList[dropdownIndex];
            if (!item || !item.id || !item[this.uniqueIdField]) return;
            
            const currentOptions = this.dropdownOptions.get(item[this.uniqueIdField]) || [];
            const selectedItem = this.selectedItemsCache.get(item.id);
            
            // If selected item is not in current options, add it
            if (selectedItem && !currentOptions.some(opt => opt.id === item.id)) {
                const updatedOptions = [selectedItem, ...currentOptions];
                this.dropdownOptions.set(item[this.uniqueIdField], updatedOptions);
            }
        },
        
        /**
         * Update the cache of selected items
         */
        updateSelectedItemsCache() {
            // Cache selected items for quick lookup
            this.itemList.forEach(item => {
                if (item.id && item.fullname) {
                    this.selectedItemsCache.set(item.id, {
                        id: item.id,
                        fullname: item.fullname
                    });
                }
            });
        },
        
        /**
         * Generate unique ID for new items
         */
        generateUniqueId() {
            return Date.now() + '-' + Math.floor(Math.random() * 100);
        },
        
        /**
         * Add new item to the list
         */
        addItem() {
            const newItem = {
                id: null,
                [this.uniqueIdField]: this.generateUniqueId(),
                [this.displayIndexField]: this.nextIndex
            };
            this.itemList.push(newItem);
            this.nextIndex++;
            
            // Initialize options for the new dropdown
            const newIndex = this.itemList.length - 1;
            this.initializeDropdownOptions(newIndex);
        },
        
        /**
         * Remove item from the list
         */
        removeItem(itemIndex) {
            // Clean up dropdown-specific data
            const uniqueId = this.itemList[itemIndex]?.[this.uniqueIdField];
            if (uniqueId) {
                this.dropdownOptions.delete(uniqueId);
                this.debounceTimers.delete(uniqueId);
            }
            
            this.itemList.splice(itemIndex, 1);
        },
        
        /**
         * Validate uniqueness of selections
         */
        validateUniqueness(currentIndex, itemType) {
            const currentId = this.itemList[currentIndex]?.id;
            if (
                currentId &&
                this.itemList.filter(item => item.id === currentId).length > 1
            ) {
                this.$toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: `${itemType} duplikat terdeteksi. Pastikan setiap ${itemType.toLowerCase()} unik.`,
                    life: 3000,
                });
                this.itemList[currentIndex].id = null;
            }
        },
        
        /**
         * Initialize item list from props
         */
        initializeItemList(propsList, uniqueIdField, displayIndexField) {
            return propsList
                ? propsList.map((item, idx) => ({
                    ...item,
                    [uniqueIdField]: item[uniqueIdField] || this.generateUniqueId(),
                    [displayIndexField]: idx + 1
                }))
                : [];
        },
        
        /**
         * Calculate next index for new items
         */
        calculateNextIndex(itemList, displayIndexField) {
            return (itemList.length > 0)
                ? Math.max(...itemList.map(item => item[displayIndexField] || 0)) + 1
                : 1;
        }
    }
}; 